import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { Injectable, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { catchError, tap, map, switchMap, take } from 'rxjs/operators';
import { Result, ResultRow } from '_models/result';
import { Answer } from '_models/question';
import { UserService } from '_services/user.service';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { SessionService } from '_services/session.service';
import { QuestionService } from '_services/question.service';
import { withScope, captureException } from '@sentry/angular';
import { Preferences } from '_models/preferences';
import { RESULT_ROW_DEFINITIONS } from '_models/result-row-definitions';

@Injectable()
export class ResultService {
  private ups = inject(UnivPrefsService);
  private userService = inject(UserService);
  private sessionService = inject(SessionService);
  private questionService = inject(QuestionService);
  private fns = inject(Functions);

  public results: ResultRow[] = [];
  public result = new BehaviorSubject<Result | null>(null);
  public readonly isLoaded = signal(false);
  public readonly isLoading = signal(false);
  public readonly errorMsg = signal<string | null>(null);
  public resultsAge = 0;

  // How recently a result must have been calculated to be served instead of
  // recalculated. Kept short so "we recalculate results each time you visit"
  // still holds for any real return visit — this only covers finishing what is
  // effectively the same sitting, e.g. a College Board login round-trip.
  private static readonly RESULT_REUSE_MS = 60 * 60 * 1000;


  // Make this a true async and return either session ID or blank string if error
  // `force` bypasses the reuse window for an explicit recalculate.
  getResults(schoolId: string, force = false): Observable<Result | null> {
    if (this.userService.isDemo) {
      this.loadDemoResults();
      return this.result;
    }

    // Incomplete answers: skip the cloud calc and keep any prior valid result.
    // The Results page shows an incomplete-sections banner instead of a hard error.
    if (this.userService.progress !== 100) {
      this.errorMsg.set(null);
      this.isLoading.set(false);
      return this.result;
    }

    const answers = this.questionService.answers();

    if (!force && this.canReuseStoredResult(schoolId, answers)) {
      console.log('Reusing restored result: answers unchanged and calculated within the last hour');
      return this.result;
    }

    const newSession = this.sessionService.isNewSession(schoolId, answers);

    if (this.isLoaded() && !newSession) {
      console.log('Results already loaded');
      return this.result;
    }

    if (newSession) {
      this.removeResults();
      return from(this.sessionService.storeSession(answers)).pipe(
        tap(sessionId => { console.log('New Session ID:', sessionId); }),
        switchMap((sessionId) => {
          return this.cloudCalcCallable(schoolId, sessionId, this.userService.isAdmin);
        })
      );
    }

    return this.cloudCalcCallable(schoolId, this.userService.sessionId, this.userService.isAdmin);
  }

  /**
   * A restored session (CB login claim, or latest-by-CB-id) arrives with its
   * result already parsed, but holds no sessionId — which isNewSession() reads
   * as "brand new", forking a duplicate session doc and a second calcApiV2 run
   * for answers that never changed.
   *
   * Serve the result we already have instead, but only when nothing changed AND
   * it was calculated inside RESULT_REUSE_MS. Admins are excluded: they are
   * normally iterating on calc rules and always want a fresh run.
   */
  private canReuseStoredResult(schoolId: string, answers: Answer[]): boolean {
    if (this.userService.isAdmin) {
      return false;
    }
    const result = this.result.value;
    if (!result || !this.isLoaded()) {
      return false;
    }
    // createdAt is stamped server-side at calculation time and persisted with
    // the result, so a restored session reports its original calculation age.
    // A negative age means a skewed clock — recalculate rather than trust it.
    const age = Date.now() - (result.createdAt ?? 0);
    if (age < 0 || age > ResultService.RESULT_REUSE_MS) {
      return false;
    }
    return this.sessionService.matchesLoadedSession(schoolId, answers);
  }

  public removeResults() {
    this.result.next(null);
    this.results = [];
    this.isLoaded.set(false);
    this.isLoading.set(false);
    this.errorMsg.set(null);
    this.resultsAge = 0;
  }

  public reCalculate() {
    this.removeResults();
    // Explicit recalculate: never served from the reuse window.
    this.getResults(this.userService.schoolId, true).pipe(take(1)).subscribe();
  }

  private cloudCalcCallable(schoolId: string, sessionId: string, adminSession: boolean): Observable<Result | null> {
    const callable = httpsCallable(this.fns, adminSession ? 'calcApiAdmin' : 'calcApiV2');
    const payload = adminSession
      ? { schoolId, sessionId, adminUsePublishedRules: false }
      : { schoolId, sessionId };
    this.isLoading.set(true);
    return from(callable(payload))
      .pipe(
        map((result: any) => {
          const res = result.data;
          return adminSession ? res?.npcResult : res;
        }),
        tap(res => this.parseResults(res as Result)),
        catchError((error: any) => {
          // Convert to HttpErrorResponse-like object for handleApiError
          const httpError = error as HttpErrorResponse;
          this.handleApiError(httpError, 'CloudCalc');
          return of(null);
        })
      );
  }

  private handleApiError(httpError: HttpErrorResponse, errorType: 'CloudCalc' | 'LegacyCalc') {
    this.isLoading.set(false);
    this.isLoaded.set(false);

    const errorFromAPI = httpError.error || '';

    console.log('HTTP Error for', errorType, 'API:', httpError.url || '');
    console.log('Status', httpError.status || '', httpError.statusText || '', errorFromAPI);
    if (httpError.status === 500) {
      this.errorMsg.set('This school is not set up completely or is currently in maintenance mode.');
    } else {
      if (errorFromAPI.includes('No INAS Result')) {
        // TODO: this message should include the session ID
        this.errorMsg.set('There is something wrong with this request.  Support has been notified.');
      } else {
        this.errorMsg.set('The servers seem to be unreachable, please try again later.');
      }
    }
    const err = new Error(httpError.message);
    err.name = httpError.name;
    withScope(scope => {
      scope.setTag('ApiError', errorType);
      scope.setExtra('API_ERROR', httpError.error);
      scope.setLevel('warning');
      scope.setFingerprint(['calculation-error']);
      captureException(err);
    });

  }





  loadDemoResults() {
    const created = Number(new Date());
    const coa = {
      tuition: 36400,
      housing: 13500,
      books: 2100,
      transportation: 0,
      personal: 1400
    };

    const result = {
      coa: coa,
      pellGrant: 4445,
      stateGrant: 0,
      institutionalGrant: 5700,
      institutionalScholarship: 10000,
      schoolDefinedFund1: 0,
      schoolDefinedFund2: 0,
      studentWork: 5500,
      studentLoan: 1000,
      consolidatedSelfHelp: 0,
      parentContribution: 1434,
      studentContribution: 200,
      parentLoan: 0,
      totalCost: 53400,
      totalGrantAid: 20145,
      netPrice: 0,
      remainingCost: 0,
      // Unused by app but sent by calc engine
      unmetNeed: 51766,
      totalAid: 26645,
      totalSelfHelp: 6500,
      nextSteps: '',
      createdAt: created,
      sai: -1500,
      saiNoNegative: 0,
      metPovertyGuidelines: false,
      institutionalMethod: true
    };


    this.computeTotals(result);

    this.result.next(result);

    const preferences = this.ups.preferences();
    if (!preferences) {
      this.errorMsg.set('Preferences not found. Please contact support.');
      this.isLoading.set(false);
      return;
    }
    this.populateTable(preferences, result);

    this.isLoaded.set(true);
  }

  parseResults(result: Result | null) {

    if (!result) {
      this.errorMsg.set('No results found. Please contact support.');
      this.isLoaded.set(true);
      this.isLoading.set(false);
      return;
    }

    const preferences = this.ups.preferences();

    if (!preferences) {
      this.errorMsg.set('Preferences not found. Please contact support.');
      this.isLoading.set(false);
      return;
    }

    this.populateTable(preferences, result);

    this.result.next(result);

    // Replace population next steps from this calculation. An empty string clears a previous match.
    this.ups.addNextSteps(result.nextSteps || '');
    this.isLoaded.set(true);
    this.isLoading.set(false);
    this.errorMsg.set(null);
    this.sessionService.storeResults(result);


    const magicNumber = (1000 * 60 * 60 * 24);

    this.resultsAge = Math.max(Math.floor((Date.now() - result.createdAt) / magicNumber), 0);
  }

  private computeTotals(result: Result) {
    if (!result) {
      return;
    }
    result.totalCost = result.coa.tuition + result.coa.housing
      + result.coa.books + result.coa.transportation + result.coa.personal;
    result.totalGrantAid = result.pellGrant + result.stateGrant
      + result.institutionalGrant + result.institutionalScholarship
      + result.schoolDefinedFund1 + result.schoolDefinedFund2;
    result.netPrice = result.totalCost - result.totalGrantAid;
  }

  private getResultValue(result: Result, fieldId: string): number {
    const coa = result.coa;
    switch (fieldId) {
      case 'tuition': return coa.tuition;
      case 'housing': return coa.housing;
      case 'books': return coa.books;
      case 'transportation': return coa.transportation;
      case 'personal': return coa.personal;
      case 'totalCoa': return result.totalCost;
      case 'pellGrant': return result.pellGrant;
      case 'stateGrant': return result.stateGrant;
      case 'institutionalGrant': return result.institutionalGrant;
      case 'institutionalScholarship': return result.institutionalScholarship;
      case 'schoolDefinedFund1': return result.schoolDefinedFund1;
      case 'schoolDefinedFund2': return result.schoolDefinedFund2;
      case 'studentLoan': return result.studentLoan;
      case 'studentWork': return result.studentWork;
      case 'parentLoan': return result.parentLoan;
      case 'consolidatedSelfHelp': return result.consolidatedSelfHelp;
      case 'parentContribution': return result.parentContribution;
      case 'studentContribution': return result.studentContribution;
      case 'sai': return result.sai;
      default: return 0;
    }
  }

  private populateTable(preferences: Preferences, result: Result) {
    if (!result) {
      return;
    }
    this.results = RESULT_ROW_DEFINITIONS.map(def => {
      const name = def.schoolLabelKey
        ? (preferences[def.schoolLabelKey] as string) ?? ''
        : (def.name ?? '');
      return {
        section: def.section,
        fieldId: def.fieldId,
        name,
        value: this.getResultValue(result, def.fieldId),
        ordinal: def.ordinal,
        helpText: def.helpText,
      };
    });

    if (this.results.filter(row => row.section === 'AID' && row.value > 0).length > 1) {
      this.results.push({ section: 'AID', fieldId: 'totalGrantAid', name: 'Total', value: result.totalGrantAid, ordinal: 14, helpText: '' });
    }
    if (this.results.filter(row => row.section === 'SELF' && row.value > 0).length > 1) {
      this.results.push({ section: 'SELF', fieldId: 'totalStudent', name: 'Total', value: result.studentLoan + result.studentWork + result.parentLoan + result.consolidatedSelfHelp, ordinal: 22, helpText: '' });
    }
    if (this.results.filter(row => row.section === 'EFC' && row.value > 0).length > 1) {
      this.results.push({ section: 'EFC', fieldId: 'totalEFC', name: 'Total', value: result.parentContribution + result.studentContribution, ordinal: 27, helpText: '' });
    }
  }
}
